import Debug from 'debug';
import { injectable, inject } from 'inversify';
import { TrackingPlugin, StartError, Result, ok, TrackingDetails,
         PluginDetails, startError } from '../../../tracking-api';
import { Command, Query } from '../../../common'
import { Scorm2004Client } from '../client';
import { EmptyPhase } from './empty-phase';
import { InitializedPhase, InitializedPhaseFactorySymbol } from './initialized-phase';
import { HandledEvents } from './handled-events';
import { Scorm2004ApiResolver } from '../resolver';
import { ScormErrorNotificationService, ScormErrorNotificationServiceSymbol } from '../client/scorm-notification-service';

const d = Debug('scorm2004-plugin');

enum Phase {
  none,
  initialized,
  terminated,
  error
}

type PhaseInstance = EmptyPhase | InitializedPhase;

@injectable()
export class Scorm2004TrackingPlugin implements TrackingPlugin<HandledEvents> {

  private client: Scorm2004Client;
  private phase: Phase = Phase.none;
  private phaseInstance: PhaseInstance = new EmptyPhase();

  constructor(private readonly apiResolver: Scorm2004ApiResolver,
              @inject(InitializedPhaseFactorySymbol)
              private readonly initializedPhaseFactory: (client: Scorm2004Client) => InitializedPhase,
              public readonly details: PluginDetails) {}

  public async start(details: TrackingDetails): Promise<Result<true, StartError>> {

    const api = this.apiResolver.resolve(details);
    if (!api.success) {
      return startError('could-not-connect-to-api', api.error);
    }

    this.client = new Scorm2004Client(api.value);
    d('successfully connected to the api');

    const initializedPhase = this.initializedPhaseFactory(this.client);
    await this.changePhase(Phase.initialized, initializedPhase);
    
    return ok<true>(true);
  }

  public async finish() {
    if (this.phase !== Phase.initialized) {
      throw new Error(`Invalid current state '${this.phase}'.`);
    }

    await this.changePhase(Phase.terminated, new EmptyPhase());
    return ok<true>(true);
  }

  public async handleEvent(event: HandledEvents) {

    d('SCORM handled event', event);
    await this.phaseInstance.handleEvent(event);
  }

  public execute(command: Command) {

    d('SCORM execute', command);
    this.phaseInstance.execute(command)
  }

  public query<T extends string, TPayload, TResult>(query: Query<T, TPayload, TResult>): Promise<TResult> {
    d('SCORM query', query);

    return this.phaseInstance.query(query)
  }

  private async changePhase(phase: Phase, instance: PhaseInstance) {
    await this.phaseInstance.exit();
    this.phase = phase;
    this.phaseInstance = instance;
    await this.phaseInstance.enter();
  }
}
